/**
 * Pages of the original file a call is sent, when it does not need the whole
 * file. The classifier lists the pages of each segment; a call that reads some
 * segments is sent a file where only their pages have content. Every other
 * page is blank, so page numbers stay those of the original file.
 */

/**
 * The pages to send, sorted and without repeats. Null when the whole file must
 * be sent: a segment has no pages, a page is outside the file, or the pages
 * already cover the whole file.
 */
export const projectFileExcerptPages = (
  segmentPages: readonly (readonly number[] | undefined)[],
  numPages: number,
): number[] | null => {
  if (segmentPages.length === 0 || numPages < 1) return null;
  const pages = new Set<number>();
  for (const list of segmentPages) {
    if (list == null || list.length === 0) return null;
    for (const page of list) {
      if (!Number.isInteger(page) || page < 1 || page > numPages) return null;
      pages.add(page);
    }
  }
  if (pages.size >= numPages) return null;
  return [...pages].sort((a, b) => a - b);
};

/** Pages as short ranges for a prompt, for example `11, 13–21`. */
export const formatProjectFilePageList = (pages: readonly number[]): string => {
  const ranges: string[] = [];
  let start: number | null = null;
  let previous: number | null = null;
  const flush = () => {
    if (start === null || previous === null) return;
    ranges.push(start === previous ? String(start) : `${start}–${previous}`);
  };
  for (const page of pages) {
    if (previous !== null && page === previous + 1) {
      previous = page;
      continue;
    }
    flush();
    start = page;
    previous = page;
  }
  flush();
  return ranges.join(', ');
};

/** Prompt text that tells the model which pages of the attachment have content. */
export const projectFileExcerptPromptText = (
  excerptPages: readonly number[],
): string =>
  `Only pages ${formatProjectFilePageList(excerptPages)} of the attached file have content; the other pages are left blank because this task does not need them. Page numbers are those of the original file.`;
